/**
 * Analytics type definitions
 */

import { environment } from '../../environments/environment';

// ============================================================================
// FUNNEL TRACKING INTERFACES
// ============================================================================

export interface FunnelTracker {
  SessionId: string;
  EventName: string;
  CustomerId: string;
  ApplicationId: string;
  MerchantId: string;
  FlowType: string;
  Utm_Source: string;
  Utm_Medium: string;
  Utm_Campaign: string;
  Name: string;
  Type: string;
  CreatedOn: number;
  Value: string;
  Version: string;
}

export interface GlobalParams {
  SessionId: string;
  Utm_Source: string;
  Utm_Medium: string;
  Utm_Campaign: string;
}

export interface UserDetails {
  CustomerId: string;
  ApplicationId: string;
  MerchantId: string;
  FlowType: string;
  Version: string;
}

export interface EventDetails {
  EventName: string;
  EventType: string;
  CreatedOn: number;
  EventValue: string;
  FieldType: string;
}

// ============================================================================
// FACTORY FUNCTIONS
// ============================================================================

export function createGlobalParams(): GlobalParams {
  return {
    SessionId: environment.dummyUuid,
    Utm_Source: 'Direct',
    Utm_Medium: 'ZestMoney',
    Utm_Campaign: 'Web-app'
  };
}

export function createUserDetails(customerId: string | null): UserDetails {
  return {
    CustomerId: customerId || environment.dummyUuid,
    ApplicationId: environment.dummyUuid,
    MerchantId: environment.zestMerchantId,
    FlowType: customerId ? 'myaccounts' : '_null',
    Version: 'v2'
  };
}

export function createEventDetails(
  name: string,
  type: string,
  value: string,
  field: string = '_null'
): EventDetails {
  return {
    EventName: name,
    EventType: type,
    EventValue: value,
    FieldType: field,
    CreatedOn: Date.now()
  };
}

// ============================================================================
// WEBENGAGE INTERFACES
// ============================================================================

export interface WebEngageUser {
  login: (userId: string) => void;
  logout: () => void;
  setAttribute: (key: string, value: string | number | boolean) => void;
}

export interface WebEngageInstance {
  user: WebEngageUser;
  track: (eventName: string, eventData?: Record<string, unknown>) => void;
  onReady: (callback: () => void) => void;
}

declare global {
  interface Window {
    webengage?: WebEngageInstance;
    ga?: UniversalAnalyticsGa;
    gtag?: Gtag.Gtag;
  }
}

// ============================================================================
// GOOGLE ANALYTICS INTERFACES
// ============================================================================

export interface UniversalAnalyticsGa {
  (...args: unknown[]): void;
  getAll?: () => Array<{ get: (name: string) => string }>;
}

// Google Analytics 4 / gtag types
declare namespace Gtag {
  interface Gtag {
    (command: 'config', targetId: string, config?: Record<string, unknown>): void;
    (command: 'set', config: Record<string, unknown>): void;
    (command: 'event', eventName: string, eventParams?: Record<string, unknown>): void;
    (command: 'js', config: Date): void;
  }
}

// ============================================================================
// TRACKING EVENT TYPES
// ============================================================================

export interface LoanCardTrackingProps {
  merchantName: string;
  merchantId: string;
  loanAmount: number;
  emiPaid?: number;
  emiAmount: number;
  loanStatus: string;
  loanId: string;
  isInsured?: string;
  loanCard?: string;
}

export interface PaymentTrackingProps {
  merchantName: string;
  merchantId: string;
  loanAmount: number;
  payableAmount?: number;
  paymentCategory?: string;
  precloseAmount?: number;
  loanStatus: string;
  loanId: string;
  isInsured?: string;
}

export interface PageViewTrackingProps {
  view: string;
  context?: string;
}

export interface ActionTrackingProps {
  action: string;
  view: string;
  context?: string;
}

// ============================================================================
// PREDEFINED EVENT NAMES
// ============================================================================

export const AnalyticsEvents = {
  // Loan Card Events
  ONGOING_LOANS_CARD: 'WebApp_OngoingLoans_Card',
  PAST_LOANS_CARD: 'WebApp_PastLoans_Card',
  
  // Loan Detail Events
  ONGOING_LOAN_DETAILS_VISITED: 'WebApp_OngoingLoanDetails_Visited',
  PAST_LOAN_DETAILS_VISITED: 'WebApp_PastLoanDetails_Visited',
  
  // Payment Events
  ONGOING_LOANS_PAY_EMI: 'WebApp_OngoingLoans_PayEmi',
  ONGOING_LOANS_PRECLOSE: 'WebApp_OngoingLoans_Preclose',
  ONGOING_LOAN_DETAILS_PAY_DUE: 'WebApp_OngoingLoanDetails_PayDue',
  ONGOING_LOAN_DETAILS_PRECLOSE: 'WebApp_OngoingLoanDetails_Preclose',
  ONGOING_LOAN_DETAILS_PAYMENT_CATEGORY: 'WebApp_OngoingLoanDetails_PaymentCategory',
  
  // View Events
  VIEW_ACTION: 'WebApp_View_Action',
  VIEW_VISITED: 'WebApp_View_Visited',
  
  // Lender Events
  ONGOING_LOAN_DETAILS_LENDER_SITE: 'WebApp_OngoingLoanDetails_LenderSite',
  PAST_LOAN_DETAILS_LENDER_SITE: 'WebApp_PastLoanDetails_LenderSite',
  
  // Other Events
  DOWNLOAD_APP_CLICKED: 'WebApp_DownloadApp_Clicked',
  BOLT_INTRODUCTION_LEARN_MORE: 'WebApp_BoltIntroduction_LearnMore',
  EMIS_BOLT_VIEW_BILLS: 'WebApp_EmisBolt_ViewBills',
} as const;

export type AnalyticsEventName = typeof AnalyticsEvents[keyof typeof AnalyticsEvents];
